// FR-RD14: auto-resume on launch. Mounted once in App.tsx, next to TraySync/ImpersonationBanner
// — same pattern: it gates on `useSession()` itself rather than being wrapped per-route, and
// renders nothing until there's something to say. Runs the resume state machine
// (./stateMachine.ts) over IndexedDB (web) or disk.rs + the upload.json sidecar
// (desktop/mobile — see ./diskAdapter.ts), without any user action.
import { useEffect, useRef, useState, type CSSProperties, type Dispatch, type SetStateAction } from "react";
import { useLocation } from "react-router-dom";
import { Button, Callout, UploadStatus } from "@grackle/ui";
import { discardDiskSession, isTauri } from "../../desktop";
import { useSession } from "../../session";
import { deleteLocalSession as deleteWebLocalSession } from "../persist/chunkStore";
import { diskLocalSource } from "./diskAdapter";
import { createRealQueueFactory, realRecoveryApi } from "./realQueue";
import { runRecovery } from "./stateMachine";
import type { LocalSource, RecoveryEvent } from "./types";
import { webLocalSource } from "./webAdapter";

interface Notice {
  sessionId: string;
  title: string;
  phase: "uploading" | "completed" | "pending" | "failed";
}

// The on-launch sweep is one-shot per app launch — not per mount/route-change, and not
// re-run by React StrictMode's dev double-invoke. A module-level flag (rather than a ref) is
// deliberate: RecoveryProvider itself never unmounts while the app is open, but this also
// guards against a second instance (there should never be one) racing the same local sessions.
let ranThisLaunch = false;

/** Scope item 4 (mobile/offline publish): a `pending_upload` session left by a prior sweep
 * retries on the `online` event, on app resume (`visibilitychange` -> visible), and on this
 * backoff timer — covers a flaky connection that never fires a clean `online` transition. */
const BACKGROUND_RETRY_MS = 45_000;

/** Paths this must never run on: the HUD has no app shell at all (PR4), and a public share
 * link is reachable signed out — neither should start touching this browser's own local
 * recordings. */
function isExcludedPath(pathname: string): boolean {
  return pathname === "/hud" || pathname.startsWith("/s/");
}

export default function RecoveryProvider(): JSX.Element | null {
  const { user, loading } = useSession();
  const location = useLocation();
  const [notices, setNotices] = useState<Notice[]>([]);
  // Upload progress (0-1) per local session, from the recovery queue's own snapshots — so the
  // notice shows real movement instead of an empty bar that reads as "stalled".
  const [progress, setProgress] = useState<Record<string, number>>({});
  const localSourceRef = useRef<LocalSource>(isTauri() ? diskLocalSource : webLocalSource);
  // Guards every trigger below (launch, online, visibility, backoff) against overlapping runs
  // — runRecovery already processes sessions one at a time, but two concurrent *sweeps* could
  // still both try the same pending_upload session.
  const runningRef = useRef(false);

  useEffect(() => {
    if (loading || !user) return;
    if (isExcludedPath(location.pathname)) return;

    const trigger = () => {
      if (runningRef.current) return;
      runningRef.current = true;
      void runRecovery({
        localSource: localSourceRef.current,
        queueFactory: createRealQueueFactory((sessionId, snap) => {
          if (!sessionId || snap.bytesRecorded <= 0) return;
          const fraction = Math.min(1, snap.bytesAccepted / snap.bytesRecorded);
          setProgress((prev) => (prev[sessionId] === fraction ? prev : { ...prev, [sessionId]: fraction }));
        }),
        api: realRecoveryApi,
        ownerId: user.id,
        onEvent: (event: RecoveryEvent) => applyEvent(event, setNotices),
      }).finally(() => {
        runningRef.current = false;
      });
    };

    if (!ranThisLaunch) {
      ranThisLaunch = true;
      trigger();
    }

    // Scope item 4: retry a pending_upload session when connectivity actually comes back,
    // when the app/tab is foregrounded again, and on a plain backoff timer as a fallback for
    // a connection that "works" (no online event) but keeps failing requests.
    window.addEventListener("online", trigger);
    const onVisible = () => {
      if (document.visibilityState === "visible") trigger();
    };
    document.addEventListener("visibilitychange", onVisible);
    const interval = setInterval(trigger, BACKGROUND_RETRY_MS);

    return () => {
      window.removeEventListener("online", trigger);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
    // Intentionally keyed on sign-in state and path exclusion, not re-subscribing per notice
    // change — see ranThisLaunch/runningRef for how re-entrancy within a launch is handled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, location.pathname]);

  async function exportFile(sessionId: string, title: string) {
    try {
      const chunks = await localSourceRef.current.readChunks(sessionId);
      const blob = new Blob(chunks);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title || "recording"}.webm`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // The data that made this session unreadable in the first place is still unreadable —
      // nothing more to offer here.
    }
  }

  async function discard(sessionId: string) {
    if (!window.confirm("Discard this recording? This can't be undone.")) return;
    if (isTauri()) await discardDiskSession(sessionId);
    else await deleteWebLocalSession(sessionId);
    setNotices((prev) => prev.filter((n) => n.sessionId !== sessionId));
  }

  if (isExcludedPath(location.pathname) || notices.length === 0) return null;

  return (
    <div className="recovery-notices" style={recoveryNoticesStyle}>
      <Callout tone="now" icon="cloud-arrow-up">
        {notices.every((n) => n.phase === "failed")
          ? "A recording was interrupted and couldn't be recovered automatically"
          : "A recording was interrupted — resuming upload"}
      </Callout>
      {notices.map((n) => (
        <Callout
          key={n.sessionId}
          tone={n.phase === "failed" ? "danger" : n.phase === "completed" ? "success" : "now"}
          icon={n.phase === "failed" ? "warning-circle" : n.phase === "completed" ? "check-circle" : "cloud-arrow-up"}
        >
          <div>{noticeLabel(n)}</div>
          {n.phase === "uploading" ? (
            <UploadStatus state="uploading" progress={progress[n.sessionId] ?? 0} />
          ) : n.phase === "pending" ? (
            browserOffline() ? (
              <UploadStatus state="offline" />
            ) : (
              <UploadStatus
                state="uploading"
                progress={progress[n.sessionId] ?? 0}
                label="Saved on this device · retrying the upload"
              />
            )
          ) : null}
          {n.phase === "failed" ? (
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Button size="sm" variant="secondary" onClick={() => void exportFile(n.sessionId, n.title)}>
                Export file
              </Button>
              <Button size="sm" variant="danger" onClick={() => void discard(n.sessionId)}>
                Discard
              </Button>
            </div>
          ) : null}
        </Callout>
      ))}
    </div>
  );
}

const recoveryNoticesStyle: CSSProperties = {
  position: "fixed",
  bottom: 16,
  right: 16,
  zIndex: 1000,
  display: "flex",
  flexDirection: "column",
  gap: 8,
  maxWidth: 360,
};

function browserOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function noticeLabel(n: Notice): string {
  switch (n.phase) {
    case "uploading":
      return `Recovered "${n.title}" — uploading`;
    case "completed":
      return `"${n.title}" uploaded`;
    case "pending":
      // Only blame the connection when the browser says it's offline — a stalled upload while
      // online is retried on the backoff timer, and "back online" would just be untrue.
      return browserOffline()
        ? `"${n.title}" will upload once you're back online`
        : `"${n.title}" didn't finish uploading — retrying automatically`;
    case "failed":
      return `Couldn't recover "${n.title}"`;
  }
}

function applyEvent(event: RecoveryEvent, setNotices: Dispatch<SetStateAction<Notice[]>>): void {
  const withoutThis = (prev: Notice[]) => prev.filter((n) => n.sessionId !== event.sessionId);
  if (event.type === "recovered") {
    setNotices((prev) => [...withoutThis(prev), { sessionId: event.sessionId, title: event.title, phase: "uploading" }]);
    return;
  }
  if (event.type === "completed") {
    setNotices((prev) => [...withoutThis(prev), { sessionId: event.sessionId, title: event.title, phase: "completed" }]);
    setTimeout(() => setNotices((prev) => withoutThis(prev)), 4000);
    return;
  }
  if (event.type === "pending") {
    setNotices((prev) => [...withoutThis(prev), { sessionId: event.sessionId, title: event.title, phase: "pending" }]);
    return;
  }
  setNotices((prev) => [...withoutThis(prev), { sessionId: event.sessionId, title: event.title, phase: "failed" }]);
}
